import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Music, Play, Pause, Upload, Check, AlertCircle } from 'lucide-react';
import { Button } from '../../ui/button';
import { musicAPI } from '../../../lib/api';
import { useTranslation } from '../../../lib/i18n';
import { toast } from 'sonner';

/**
 * Step 4: pick the background-music tracks the agent likes.
 *
 * Multi-select. Clicking a card toggles it in/out of the array. The
 * scheduler picks one of the liked tracks at random for every auto-post,
 * so picking 3-4 favourites gives the videos sonic variety without
 * forcing the agent to manage a single "default" track.
 *
 * Props
 *   selectedPresetIds   string[]           . Array of liked preset ids
 *   selectedUploadUrl   string | null      . Single uploaded MP3
 *   onPresetIdsChange(ids)
 *   onUploadSelect(url) – '' to clear
 *   onClear()          . Clear both presets and upload (= no music)
 */
export default function MusicStep({
  selectedPresetIds = [],
  selectedUploadUrl,
  onPresetIdsChange,
  onUploadSelect,
  onClear,
}) {
  const { t } = useTranslation();
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await musicAPI.getPresets();
        if (cancelled) return;
        const all = Array.isArray(res.data) ? res.data : [];
        // Show every preset the backend serves. Used to clip to a curated
        // 3, but real users wanted choice. And we render the cards in a
        // wrap-friendly grid so 6 fits cleanly.
        setPresets(all);
      } catch (err) {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Stop playback when the component unmounts.
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const togglePlay = (preset) => {
    // Single shared audio element. Switch source when the user tries a
    // different preset. Tapping the active one toggles pause/resume.
    if (playingId === preset.id && audioRef.current) {
      audioRef.current.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
    }
    const audio = new Audio(preset.url);
    audio.addEventListener('ended', () => setPlayingId(null));
    audio.play().catch(() => {
      toast.error(t('onboarding.step4.playFailed') || 'Kann das Vorschau-Audio nicht abspielen.');
      setPlayingId(null);
    });
    audioRef.current = audio;
    setPlayingId(preset.id);
  };

  const togglePresetLike = (preset) => {
    const isLiked = selectedPresetIds.includes(preset.id);
    const next = isLiked
      ? selectedPresetIds.filter((id) => id !== preset.id)
      : [...selectedPresetIds, preset.id];
    onPresetIdsChange?.(next);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await musicAPI.upload(form);
      const url = res.data?.file_url || res.data?.url;
      if (!url) throw new Error('no-url');
      onUploadSelect?.(url);
      toast.success(t('onboarding.step4.uploadSuccess') || 'Eigene Musik hochgeladen');
    } catch (err) {
      toast.error(t('onboarding.step4.uploadFailed') || 'Upload fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-[#1f4d3a] animate-spin" />
      </div>
    );
  }

  const totalSelected = selectedPresetIds.length + (selectedUploadUrl ? 1 : 0);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="inline-flex p-4 bg-[#e9efe7] rounded-2xl mb-4">
          <Music className="w-10 h-10 text-[#1f4d3a]" />
        </div>
        <h2 className="text-2xl font-bold text-[#23211a] mb-1">
          {t('onboarding.step4.title') || 'Wähle deine Musik'}
        </h2>
        <p className="text-sm text-[#6f6a5c] max-w-md mx-auto">
          {t('onboarding.step4.subtitleMulti') ||
            'Like alles was dir gefällt. Wir spielen pro Auto-Post zufällig eine deiner Lieblingsmusiken.'}
        </p>
        {totalSelected > 0 && (
          <p className="text-xs text-[#1f4d3a] mt-2">
            {t('onboarding.step4.selectedCount', { count: totalSelected }) ||
              `${totalSelected} ausgewählt`}
          </p>
        )}
      </div>

      {loadError && (
        <div className="flex items-start gap-2 text-sm text-[#9a6d1f] bg-[#f6edd8] border border-[#c7a869] rounded-lg p-3">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            {t('onboarding.step4.loadError') ||
              'Presets konnten nicht geladen werden. Du kannst deine eigene Musik hochladen oder den Schritt überspringen.'}
          </span>
        </div>
      )}

      {/* Preset grid. 1 col on mobile, 2 on tablet+. Wraps cleanly for 6. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {presets.map((preset) => {
          const isSelected = selectedPresetIds.includes(preset.id);
          const isPlaying = playingId === preset.id;
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => togglePresetLike(preset)}
              className={`text-left p-4 rounded-xl border-2 transition-all ${
                isSelected
                  ? 'border-[#1f4d3a] bg-[#e9efe7] shadow-[0_0_0_4px_rgba(139,92,246,0.1)]'
                  : 'border-[#d8d0bd] bg-[#fffdf7] hover:border-[#1f4d3a]/50'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="font-medium text-[#23211a]">{preset.name}</p>
                {isSelected && <Check className="w-4 h-4 text-[#1f4d3a] shrink-0" />}
              </div>
              <p className="text-xs text-[#6f6a5c] mt-1 mb-3 line-clamp-2">
                {preset.description}
              </p>
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  togglePlay(preset);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    togglePlay(preset);
                  }
                }}
                className={`inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border transition ${
                  isPlaying
                    ? 'border-[#1f4d3a] text-[#1f4d3a] bg-[#dfe8db]'
                    : 'border-[#d8d0bd] text-[#4a463b] hover:border-[#1f4d3a]/50'
                }`}
              >
                {isPlaying ? (
                  <>
                    <Pause className="w-3.5 h-3.5" />
                    {t('onboarding.step4.pause') || 'Pause'}
                  </>
                ) : (
                  <>
                    <Play className="w-3.5 h-3.5" />
                    {t('onboarding.step4.play') || 'Anhören'}
                  </>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Upload option. Sits alongside presets in the rotation rather
          than replacing them. If the user has both presets liked AND an
          upload, the scheduler picks randomly across all of them.
          Mobile layout stacks vertically so the descriptive text never
          gets squeezed by the button (the previous flex-row layout was
          pushing the "Hochladen" button off the right edge on phones). */}
      <div className="border-t border-[#e3dccb] pt-5 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-[#23211a]">
              {t('onboarding.step4.uploadTitle') || 'Eigene Musik verwenden'}
            </p>
            <p className="text-xs text-[#6f6a5c]">
              {t('onboarding.step4.uploadSubtitleMulti') ||
                'MP3 hochladen. Wird zur zufälligen Auswahl dazugepackt.'}
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/mpeg,audio/mp3"
            className="hidden"
            onChange={handleFileChange}
          />
          <Button
            type="button"
            variant="outline"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            className="border-[#d8d0bd] text-[#23211a] hover:bg-[#efe9db] w-full sm:w-auto sm:shrink-0"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                {t('common.uploading') || 'Lade hoch…'}
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-1.5" />
                {t('onboarding.step4.uploadBtn') || 'Hochladen'}
              </>
            )}
          </Button>
        </div>

        {selectedUploadUrl && (
          <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-[#1f4d3a]/50 bg-[#e9efe7]">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Check className="w-4 h-4 text-[#1f4d3a] shrink-0" />
              <span className="text-xs text-[#23211a] truncate">
                {selectedUploadUrl.split('/').pop() || selectedUploadUrl}
              </span>
            </div>
            <button
              type="button"
              onClick={() => onUploadSelect?.('')}
              className="text-xs text-[#6f6a5c] hover:text-[#23211a] shrink-0"
            >
              {t('common.remove') || 'Entfernen'}
            </button>
          </div>
        )}
      </div>

      {/* "No music" escape hatch. Clears all presets + upload. */}
      <div className="text-center">
        <button
          type="button"
          onClick={() => onClear?.()}
          className={`text-xs transition ${
            totalSelected === 0
              ? 'text-[#4a463b] underline underline-offset-2'
              : 'text-[#8a8474] hover:text-[#4a463b]'
          }`}
        >
          {t('onboarding.step4.noMusic') || 'Keine Musik verwenden'}
        </button>
      </div>
    </div>
  );
}
